import { useCallback, useState } from 'react';

import type { FilterItem } from 'Libs/filter/types';

export type StartDateFilter = {
  startDate: Date | null;
};

export type EndDateFilter = {
  endDate: Date | null;
};

export type AudienceGroupsFilter = {
  audienceGroups: number[];
};

export type CampaignsFilter = {
  campaigns: number[];
};

export type CoursesFilter = {
  courses: number[];
};

export type AllFilters = StartDateFilter & EndDateFilter & AudienceGroupsFilter & CampaignsFilter & CoursesFilter;
export type Filters = Partial<AllFilters>;

const FILTER_SELECT_MAP = {
  audienceGroup: 'audienceGroups',
  campaign: 'campaigns',
  course: 'courses',
};

type FilterSelectKeys = keyof typeof FILTER_SELECT_MAP;
type FilterSelectValues = (typeof FILTER_SELECT_MAP)[FilterSelectKeys];

const useFilters = <T extends Filters>() => {
  const [selectedFilters, setSelectedFilters] = useState<FilterItem[]>([]);
  const [filters, setFilters] = useState<Filters>({
    startDate: null,
    endDate: null,
    audienceGroups: [],
    campaigns: [],
    courses: [],
  });

  const handleFilterChange = useCallback(
    (filterCategory: keyof T, value: Date | FilterItem[]) => {
      if (filterCategory === 'startDate' || filterCategory === 'endDate') {
        return setFilters((prev) => ({ ...prev, [filterCategory]: value }));
      }

      if (Object.values(FILTER_SELECT_MAP).includes(filterCategory as FilterSelectValues)) {
        const existingFilters = new Set(selectedFilters.map((filter) => `${filter.category}-${filter.id}`));
        const newFilters = (value as FilterItem[]).filter(
          (filter) => !existingFilters.has(`${filter.category}-${filter.id}`),
        );
        setSelectedFilters([...selectedFilters, ...newFilters]);

        const selectedFilterIds = (value as FilterItem[]).map((item) => item.id);
        setFilters({ ...filters, [filterCategory]: selectedFilterIds });
      }
    },
    [filters, selectedFilters],
  );

  const handleDeleteFilter = useCallback(
    (filter: FilterItem) => {
      const newSelectedFilters = selectedFilters.filter(
        (item) => !(item.id === filter.id && item.category === filter.category),
      );
      setSelectedFilters(newSelectedFilters);

      if (Object.keys(FILTER_SELECT_MAP).includes(filter.category as FilterSelectKeys)) {
        const filterCategory = FILTER_SELECT_MAP[filter.category as FilterSelectKeys];

        const newFiltersItemsIds = newSelectedFilters
          .filter((item) => item.category === filter.category)
          .map((item) => item.id);

        setFilters({ ...filters, [filterCategory]: newFiltersItemsIds });
      }
    },
    [filters, selectedFilters],
  );

  return {
    selectedFilters,
    filters: filters as T,
    handleFilterChange,
    handleDeleteFilter,
  };
};

export default useFilters;
